import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Info, Loader2 } from "@fg/icons";
import type { DocPos } from "@fg/web-core";
import type { FileMenuItem } from "@fg/ui-theme/file-menu";
import { useAccessToken } from "@/auth/useAccessToken";
import { driveApi, explainSaveError, isDriveUrl, type DriveItem } from "@/api/driveApi";
import { useSaveTracker } from "@/lib/useSaveTracker";
import { CloseFileButton, FileTitle } from "@/features/shell/FileTitle";
import { unsavedChangesDialog } from "@fg/ui-theme/dialogs";
import { ShellHeaderPortal, useShellLeaveGuard } from "@/features/shell/ShellHeader";
import type { EditorLaunchState } from "@/features/home/homeModel";
import { DriveOpenDialog } from "@/features/docs/DriveOpenDialog";
import { driveFileApi } from "@/features/drive-file/driveFileApi";
import { DriveLocation } from "@/features/drive-file/DriveLocation";
import { ShareButton } from "@/features/drive-file/ShareDialog";
import { HistoryButton, VersionHistoryPanel, VIEWER_HISTORY_MESSAGE, isHistoryShortcut } from "@/features/drive-file/VersionHistoryPanel";
import { CommentsButton, CommentsPanel, isCommentsShortcut, useOpenCommentCount, type CommentsAdapter } from "@/features/drive-file/CommentsPanel";
import type { NotesHandle } from "./NotesEditor";

// The editor is its own chunk; the document engine runs in a Web Worker (@fg/web-core).
const NotesWorkspace = lazy(() => import("./NotesWorkspace").then((m) => ({ default: m.NotesWorkspace })));

const OPENABLE_EXT_RE = /\.(docx|txt)$/i;
const KNOWN_EXT_RE = /\.(docx|doc|rtf|txt|pdf|odt)$/i;
const DOCX_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
// Anything that reports a change right after a load belongs to the load.
const LOAD_SETTLE_MS = 250;

type DocFormat = "docx" | "txt";
type SaveMode = "manual" | "auto";

/** The Drive file the editor is bound to — Save writes a new version of it. */
interface TrackedFile {
    id: string;
    name: string;
    parentId: string | null;
    /** "viewer" / "editor" when someone else shared the file; unset for your own. */
    role?: string | null;
}

/** A comment points at the selected text: its start as `p:section;cell;block;offset` and the text as the quote. */
function notesCommentsAdapter(getEditor: () => NotesHandle | null): CommentsAdapter {
    const encode = (p: DocPos) => `p:${p.s};${p.t ? p.t.join(",") : "-"};${p.i};${p.o}`;
    const decode = (s: string): DocPos | null => {
        const m = /^p:(\d+);(-|\d+,\d+,\d+);(\d+);(\d+)$/.exec(s);
        if (!m) return null;
        return { s: Number(m[1]), t: m[2] === "-" ? null : (m[2]!.split(",").map(Number) as [number, number, number]), i: Number(m[3]), o: Number(m[4]) };
    };
    return {
        selectionHint: "Select text to attach the comment to it.",
        captureAnchor: () => {
            const ed = getEditor();
            const sel = ed?.selection();
            if (!ed || !sel) return null;
            return { anchor: encode(sel[0]).slice(0, 128), quote: "" };
        },
        navigate: (anchor) => {
            const ed = getEditor();
            const p = anchor ? decode(anchor) : null;
            if (!ed || !p) return false;
            void ed.select(p, p);
            return true;
        },
        describeAnchor: (anchor) => {
            const p = decode(anchor);
            return p ? `Paragraph ${p.i + 1}` : null;
        }
    };
}

export function NotesPage() {
    const [params] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const token = useAccessToken();
    // Autosave fires from a timer — read the freshest token / navigate.
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    // Launch parameters are read once: the URL is rewritten as files are opened and saved, and
    // that must never re-trigger a load.
    const launchRef = useRef({
        fileId: params.get("fileId"),
        openFile: params.get("openFile"),
        fileName: params.get("fileName"),
        localFile: (location.state as EditorLaunchState | null)?.localFile ?? null,
        template: (location.state as EditorLaunchState | null)?.template ?? null
    });

    const editorRef = useRef<NotesHandle>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const trackedRef = useRef<TrackedFile | null>(null);
    const [tracked, setTrackedState] = useState<TrackedFile | null>(null);
    const [fileName, setFileName] = useState(launchRef.current.fileName ?? "Untitled.docx");
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);
    const settleTimerRef = useRef<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const saveNowRef = useRef<(mode: SaveMode) => Promise<void>>(async () => {});
    const { status, markDirty, markClean, setSuppressed, runSave, hasUnsavedChanges, whenSaved } = useSaveTracker({
        onAutosave: () => void saveNowRef.current("auto"),
        onError: setError
    });

    // Binds the editor to a Drive file (null = unbound) and mirrors it into the URL (`?fileId=`).
    const setTracked = useCallback((file: TrackedFile | null) => {
        const previousId = trackedRef.current?.id ?? null;
        trackedRef.current = file;
        setTrackedState(file);
        if (file) setFileName(file.name);
        if ((file?.id ?? null) !== previousId) navigateRef.current({ search: file ? `?fileId=${encodeURIComponent(file.id)}` : "" }, { replace: true });
    }, []);

    // The one prompt for unsaved changes: Save (the save runs first; if it does not finish, stay), Don't save, or Cancel.
    const resolveUnsaved = useCallback(async (): Promise<boolean> => {
        await whenSaved();
        if (!hasUnsavedChanges()) return true;
        const choice = await unsavedChangesDialog(fileName);
        if (choice === "cancel") return false;
        if (choice === "save") {
            await saveNowRef.current("manual");
            await whenSaved();
            if (hasUnsavedChanges()) return false;
        }
        return true;
    }, [fileName, hasUnsavedChanges, whenSaved]);
    useShellLeaveGuard(resolveUnsaved);

    // Close (top bar X / File → Close) → home. A save in flight finishes first.
    const [closing, setClosing] = useState(false);
    const closingRef = useRef(false);
    const closeFile = useCallback(async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        setClosing(true);
        try {
            await whenSaved();
            if (!(await resolveUnsaved())) return;
            markClean();
            navigateRef.current("/", { replace: true });
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    }, [markClean, resolveUnsaved]);

    /** Loads file bytes into the engine: .docx natively, .txt as a plain document. */
    const loadBlob = useCallback(async (ed: NotesHandle, blob: Blob, name: string) => {
        if (/\.txt$/i.test(name)) {
            await ed.load(null);
            const text = (await blob.text()).replace(/\r\n?/g, "\n");
            await ed.edit(async (d, a) => {
                let c = a;
                const parts = text.split("\n");
                for (let i = 0; i < parts.length; i++) {
                    if (i > 0) c = (await d.enter(c)).pos;
                    if (parts[i]) c = (await d.insertText(c, parts[i]!)).pos;
                }
                return await d.firstPos();
            }, "Open");
        } else {
            await ed.load(await blob.arrayBuffer());
        }
    }, []);

    // Wraps every load. Change reports the load itself causes are ignored; a failed load leaves the previous document alone.
    const runLoad = useCallback(async (load: (ed: NotesHandle) => Promise<void>): Promise<boolean> => {
        const ed = editorRef.current;
        if (!ed) return false;
        if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        setSuppressed(true);
        try {
            await load(ed);
            markClean();
            return true;
        } catch (err) {
            setError(errorMessage(err));
            return false;
        } finally {
            loadingRef.current = false;
            setLoading(false);
            settleTimerRef.current = window.setTimeout(() => {
                settleTimerRef.current = null;
                setSuppressed(false);
            }, LOAD_SETTLE_MS);
        }
    }, [markClean, setSuppressed]);

    const openDriveFile = useCallback((file: TrackedFile) => runLoad(async (ed) => {
        if (!OPENABLE_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
        const blob = await driveApi.downloadBlob(file.id, tokenRef.current);
        await loadBlob(ed, blob, file.name);
        setTracked(file);
    }), [loadBlob, runLoad, setTracked]);

    const onLocalPick: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file || !(await resolveUnsaved())) return;
        void runLoad(async (ed) => {
            if (!OPENABLE_EXT_RE.test(file.name)) throw new Error(unsupportedMessage(file.name));
            await loadBlob(ed, file, file.name);
            setTracked(null);
            setFileName(file.name);
        });
    };

    // The editor reports ready once its engine is up; then the launch request runs.
    const loadInitial = useCallback(() => {
        if (!editorRef.current) return;
        const { fileId, openFile, fileName: launchName, localFile, template } = launchRef.current;
        if (template) {
            // A home-screen template: a new, unsaved document (the template gallery comes with the Insert tab).
            navigateRef.current({ search: "" }, { replace: true });
            void runLoad(async (ed) => {
                await ed.load(null);
                setTracked(null);
                setFileName("Untitled.docx");
            });
            return;
        }
        if (localFile) {
            navigateRef.current({ search: "" }, { replace: true });
            void runLoad(async (ed) => {
                if (!OPENABLE_EXT_RE.test(localFile.name)) throw new Error(unsupportedMessage(localFile.name));
                await loadBlob(ed, localFile, localFile.name);
                setTracked(null);
                setFileName(localFile.name);
            });
            return;
        }
        if (fileId) {
            navigateRef.current({ search: `?fileId=${encodeURIComponent(fileId)}` }, { replace: true });
            void runLoad(async (ed) => {
                const meta = await driveApi.getItem(fileId, tokenRef.current);
                setFileName(meta.name);
                if (!OPENABLE_EXT_RE.test(meta.name)) throw new Error(unsupportedMessage(meta.name));
                const blob = await driveApi.downloadBlob(meta.id, tokenRef.current);
                await loadBlob(ed, blob, meta.name);
                setTracked(toTracked(meta));
            });
        } else if (openFile) {
            navigateRef.current({ search: "" }, { replace: true });
            const name = launchName ?? "document.docx";
            void runLoad(async (ed) => {
                if (!OPENABLE_EXT_RE.test(name)) throw new Error(unsupportedMessage(name));
                // Only Drive gets the bearer token — `openFile` comes from the URL.
                const headers = new Headers();
                if (tokenRef.current && isDriveUrl(openFile)) headers.set("Authorization", `Bearer ${tokenRef.current}`);
                const res = await fetch(openFile, { headers, credentials: "omit" });
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                await loadBlob(ed, await res.blob(), name);
                setFileName(name);
            });
        }
    }, [loadBlob, runLoad, setTracked]);

    // Save As → Drive, or a Save that can't version the open file: creates a new file in the open file's folder.
    const saveAsNewDriveFile = useCallback(async (baseName: string, type: DocFormat) => {
        const ed = editorRef.current;
        if (!ed) return;
        await runSave(async () => {
            const blob = await renderDocument(ed, type);
            const saved = await driveApi.uploadBlob(blob, `${baseName}.${type}`, trackedRef.current?.parentId ?? null, tokenRef.current);
            setTracked(toTracked(saved));
        });
    }, [runSave, setTracked]);

    // Merged letters or a sheet of labels: their own file next to this one in Drive (or a download when this one is not in Drive).
    const newDriveDocument = useCallback(async (bytes: ArrayBuffer, name: string) => {
        const blob = new Blob([bytes], { type: DOCX_TYPE });
        const current = trackedRef.current;
        if (!current) {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `${name}.docx`;
            a.click();
            URL.revokeObjectURL(a.href);
            return `${name}.docx downloaded`;
        }
        const saved = await driveApi.uploadBlob(blob, `${name}.docx`, current.parentId ?? null, tokenRef.current);
        return `Saved to Drive as ${saved.name}`;
    }, []);

    const saveNow = useCallback(async (mode: SaveMode) => {
        const ed = editorRef.current;
        if (!ed || loadingRef.current) return;
        const current = trackedRef.current;
        if (!current) {
            if (mode === "manual") setSaveAsOpen(true);
            return;
        }
        const format = versionFormat(current.name);
        if (!format) {
            if (mode === "manual") await saveAsNewDriveFile(stripExtension(current.name), "docx");
            return;
        }
        if (mode === "auto" && current.role === "viewer") return;
        if (mode === "manual") setError(null);
        await runSave(async () => {
            const blob = await renderDocument(ed, format);
            const saved = await driveApi.uploadVersion(current.id, blob, current.name, tokenRef.current).catch((err: unknown) => { throw explainSaveError(err, current.role); });
            if (trackedRef.current?.id === saved.id) setTracked(toTracked(saved));
        });
    }, [runSave, saveAsNewDriveFile, setTracked]);
    saveNowRef.current = saveNow;

    // Version history — owners and editors of a Drive file only.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [commentsOpen, setCommentsOpen] = useState(false);
    const openHistory = useCallback(() => {
        const current = trackedRef.current;
        if (!current) setError("Save this document to Drive to keep a version history.");
        else if (current.role === "viewer") setError(VIEWER_HISTORY_MESSAGE);
        else {
            setCommentsOpen(false);
            setHistoryOpen(true);
        }
    }, []);
    useEffect(() => {
        if (!tracked || tracked.role === "viewer") setHistoryOpen(false);
        if (!tracked) setCommentsOpen(false);
    }, [tracked]);

    const commentCount = useOpenCommentCount(tracked?.id ?? null);
    const commentsAdapter = useMemo(() => notesCommentsAdapter(() => editorRef.current), []);
    const openComments = useCallback(() => {
        if (!trackedRef.current) {
            setError("Save this document to Drive to comment on it.");
            return;
        }
        setHistoryOpen(false);
        setCommentsOpen(true);
    }, []);

    const restoreVersion = useCallback(async (versionId: string, discard: boolean) => {
        const current = trackedRef.current;
        if (!current) throw new Error("This document isn't in Drive.");
        if (!discard && hasUnsavedChanges()) throw new Error("Save or discard your changes before restoring.");
        if (discard) markClean();
        let restored: DriveItem;
        try {
            restored = await driveFileApi.restoreVersion(current.id, versionId, tokenRef.current);
        } catch (err) {
            if (discard) markDirty();
            throw err;
        }
        const ok = await openDriveFile({ ...toTracked(restored), role: restored.role ?? current.role ?? null });
        if (!ok) throw new Error("The version was restored in Drive, but the editor couldn't reload it — reopen the file to see it.");
    }, [hasUnsavedChanges, markClean, markDirty, openDriveFile]);

    // Ctrl+S → Save; Ctrl+Shift+S → Save As; Ctrl+Alt+Shift+H → Version history
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isHistoryShortcut(e)) {
                e.preventDefault();
                openHistory();
                return;
            }
            if (isCommentsShortcut(e)) {
                e.preventDefault();
                openComments();
                return;
            }
            if (e.key.toLowerCase() === "s" && (e.ctrlKey || e.metaKey) && !e.altKey) {
                e.preventDefault();
                if (e.shiftKey) setSaveAsOpen(true);
                else void saveNowRef.current("manual");
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [openHistory, openComments]);

    const renameFile = useCallback(async (typed: string) => {
        const current = trackedRef.current;
        const next = keepExtension(typed, current?.name ?? fileName);
        if (!current) {
            setFileName(next);
            return;
        }
        const updated = await driveApi.renameItem(current.id, next, tokenRef.current);
        setTracked(toTracked(updated));
    }, [fileName, setTracked]);

    const [driveDialogOpen, setDriveDialogOpen] = useState(false);
    const openFromDrive = useCallback(() => setDriveDialogOpen(true), []);
    const pickDriveFile = useCallback(async (item: DriveItem) => {
        setDriveDialogOpen(false);
        if (!(await resolveUnsaved())) return;
        void openDriveFile(toTracked(item));
    }, [resolveUnsaved, openDriveFile]);

    const handleDownload = useCallback(() => {
        const ed = editorRef.current;
        if (!ed) return;
        setError(null);
        void downloadDocument(ed, stripExtension(fileName), "docx").catch((err: unknown) => setError(errorMessage(err)));
    }, [fileName]);

    const newDocument = useCallback(async () => {
        if (!(await resolveUnsaved())) return;
        void runLoad(async (ed) => {
            await ed.load(null);
            setTracked(null);
            setFileName("Untitled.docx");
        });
    }, [resolveUnsaved, runLoad, setTracked]);

    const openLocal = useCallback(() => fileInputRef.current?.click(), []);
    const [saveAsOpen, setSaveAsOpen] = useState(false);

    const handleSaveAsConfirm = useCallback(async (target: "drive" | "computer", name: string, type: DocFormat) => {
        setSaveAsOpen(false);
        const ed = editorRef.current;
        if (!ed) return;
        setError(null);
        const cleanName = stripExtension(name || "Untitled");
        if (target === "computer") {
            try {
                await downloadDocument(ed, cleanName, type);
            } catch (err) {
                setError(errorMessage(err));
            }
            return;
        }
        await saveAsNewDriveFile(cleanName, type);
    }, [saveAsNewDriveFile]);

    const fileMenu = useMemo<FileMenuItem[]>(() => [
        { id: "new", label: "New", shortcut: "Ctrl+N", onSelect: newDocument },
        { id: "open", label: "Open…", shortcut: "Ctrl+O", onSelect: openLocal },
        { id: "open-drive", label: "Open from Drive", onSelect: openFromDrive },
        { id: "save", label: "Save", shortcut: "Ctrl+S", divider: true, onSelect: () => void saveNowRef.current("manual") },
        { id: "save-as", label: "Save As…", shortcut: "Ctrl+Shift+S", onSelect: () => setSaveAsOpen(true) },
        { id: "download", label: "Download a copy (.docx)", onSelect: handleDownload },
        { id: "history", label: "Version history", divider: true, onSelect: openHistory },
        { id: "close", label: "Close", divider: true, onSelect: () => void closeFile() }
    ], [newDocument, openLocal, openFromDrive, handleDownload, openHistory, closeFile]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <ShellHeaderPortal>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <FileTitle
                        name={fileName}
                        status={status}
                        inDrive={tracked !== null}
                        loading={loading}
                        onRename={renameFile}
                        onRetry={() => void saveNowRef.current("manual")}
                        location={tracked ? <DriveLocation fileId={tracked.id} parentId={tracked.parentId} shared={Boolean(tracked.role)} tone="onPrimary" className="hidden sm:flex" /> : null}
                        accessory={
                            <>
                                {tracked ? <HistoryButton tone="onPrimary" onClick={openHistory} disabledReason={tracked.role === "viewer" ? VIEWER_HISTORY_MESSAGE : null} /> : null}
                                <CommentsButton file={tracked} tone="onPrimary" openCount={commentCount.count} active={commentsOpen} onClick={commentsOpen ? () => setCommentsOpen(false) : openComments} />
                            </>
                        }
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-2 pl-1">
                        <ShareButton file={tracked} tone="onPrimary" />
                        <CloseFileButton appName="Notes" busy={closing} onClose={() => void closeFile()} />
                    </div>
                </div>
            </ShellHeaderPortal>
            {historyOpen && tracked ? (
                <VersionHistoryPanel file={tracked} onClose={() => setHistoryOpen(false)} refreshKey={status} saving={status === "saving"} hasUnsavedChanges={hasUnsavedChanges} onRestore={restoreVersion} />
            ) : null}
            <input ref={fileInputRef} type="file" accept=".docx,.txt" hidden onChange={onLocalPick} />
            {error ? (
                <p role="alert" className="flex shrink-0 items-start gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{error}</span>
                </p>
            ) : null}
            <div className="relative flex min-h-0 flex-1">
                <div className="relative min-h-0 min-w-0 flex-1">
                    <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading editor…</div>}>
                        <NotesWorkspace ref={editorRef} fileMenu={fileMenu} onReady={loadInitial} onChange={markDirty} onComment={openComments} onNewDocument={newDriveDocument} fileName={tracked?.name} />
                    </Suspense>
                </div>
                {commentsOpen && tracked ? <CommentsPanel key={tracked.id} file={tracked} adapter={commentsAdapter} docked onClose={() => setCommentsOpen(false)} onOpenCountChange={commentCount.set} /> : null}
            </div>
            {driveDialogOpen ? <DriveOpenDialog onClose={() => setDriveDialogOpen(false)} onPick={pickDriveFile} /> : null}
            {saveAsOpen ? <SaveAsDialog defaultName={stripExtension(fileName)} defaultType={inferType(fileName)} onClose={() => setSaveAsOpen(false)} onConfirm={handleSaveAsConfirm} /> : null}
        </div>
    );
}

/** The live document as file bytes: .docx written by the engine, or the text as .txt. */
async function renderDocument(ed: NotesHandle, type: DocFormat): Promise<Blob> {
    if (type === "txt") return new Blob([await ed.plainText()], { type: "text/plain" });
    const bytes = await ed.save();
    if (bytes.byteLength === 0) throw new Error("The document came out empty — nothing was saved.");
    return new Blob([bytes], { type: DOCX_TYPE });
}

/** Save to the computer: render and download. */
async function downloadDocument(ed: NotesHandle, baseName: string, type: DocFormat): Promise<void> {
    const blob = await renderDocument(ed, type);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}.${type}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function inferType(name: string): DocFormat {
    return /\.txt$/i.test(name) ? "txt" : "docx";
}

/** The format Save must write to version `name` in place, or null when that would change the file's format. */
function versionFormat(name: string): DocFormat | null {
    const ext = name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
    return ext === "docx" || ext === "txt" ? ext : null;
}

function toTracked(item: DriveItem): TrackedFile {
    return { id: item.id, name: item.name, parentId: item.parentId, role: item.role ?? null };
}

function unsupportedMessage(name: string): string {
    if (/\.doc$/i.test(name)) return `${name} is an older .doc file, which Notes can't open yet. Save it as .docx first.`;
    return `${name} can't be opened in Notes. Supported file types are .docx and .txt.`;
}

function errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

// Keeps the file's extension when the user types a bare name ("Report" → "Report.docx").
function keepExtension(typed: string, current: string): string {
    const trimmed = typed.trim();
    const ext = current.match(/\.([^./\\]+)$/)?.[1];
    if (!ext) return trimmed;
    if (trimmed.toLowerCase().endsWith(`.${ext.toLowerCase()}`) && trimmed.length > ext.length + 1) return trimmed;
    const base = trimmed.replace(KNOWN_EXT_RE, "").trim();
    if (!base) throw new Error("Enter a file name.");
    return `${base}.${ext}`;
}

function SaveAsDialog({ defaultName, defaultType, onClose, onConfirm }: { defaultName: string; defaultType: DocFormat; onClose: () => void; onConfirm: (target: "drive" | "computer", name: string, type: DocFormat) => void }) {
    const [target, setTarget] = useState<"drive" | "computer">("drive");
    const [name, setName] = useState(defaultName);
    const [type, setType] = useState<DocFormat>(defaultType);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div role="dialog" aria-label="Save As" className="flex w-[460px] max-w-[92vw] flex-col overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">Save As</h2>
                    <button type="button" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">✕</button>
                </div>
                <form id="notes-save-as-form" className="flex flex-col gap-3 p-4" onSubmit={(e) => { e.preventDefault(); if (name.trim()) onConfirm(target, name.trim(), type); }}>
                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Save to</label>
                        <div className="flex gap-2">
                            {(["drive", "computer"] as const).map((t) => (
                                <button key={t} type="button" onClick={() => setTarget(t)} className={`flex-1 rounded-md border px-3 py-2 text-sm ${target === t ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}>{t === "drive" ? "Drive" : "Computer"}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="save-as-name" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Filename</label>
                        <input id="save-as-name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
                    </div>
                    <div>
                        <label htmlFor="save-as-type" className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">File type</label>
                        <select id="save-as-type" value={type} onChange={(e) => setType(e.target.value as DocFormat)} className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                            <option value="docx">Document (.docx)</option>
                            <option value="txt">Plain text (.txt)</option>
                        </select>
                    </div>
                </form>
                <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">
                    <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm hover:bg-accent">Cancel</button>
                    <button type="submit" form="notes-save-as-form" disabled={!name.trim()} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">Save</button>
                </div>
            </div>
        </div>
    );
}

function stripExtension(name: string): string {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return "Untitled";
    return trimmed.replace(/\.[^./\\]+$/, "");
}
